import {
    CheckCircleIcon,
    CloudArrowUpIcon,
    DocumentIcon,
    ExclamationTriangleIcon,
    TrashIcon,
} from "@heroicons/react/24/outline";
import { FileId, MAX_UPLOAD_BYTES } from "@sp-crm/core";
import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { PrimaryButton } from "./primary-button";
import { SecondaryButton } from "./secondary-button";
import { Spinner } from "./spinner";
import { UploadStatusList } from "./upload-status-list";
import { formatFileSize, UploadedFile } from "./uploaded-file";

export type { UploadedFile } from "./uploaded-file";

export type UploadListMode = "persistent" | "transient";

interface InitialFile {
    id: FileId;
    name: string;
    size: number;
}

interface FileUploadProps {
    uploadList: UploadListMode;
    initial?: InitialFile[];
    uploadEndpoint: string;
    maxFiles?: number;
    maxSizePerFile?: number;
    maxTotalSize?: number;
    onFilesChange?: (files: UploadedFile[]) => void;
    disabled?: boolean;
    additionalFormData?: Record<string, string>;
}

const loadInitialFiles = (initial: InitialFile[]): UploadedFile[] => {
    return initial.map(file => ({
        id: `initial-${file.id}`,
        name: file.name,
        size: file.size,
        status: "completed",
        progress: 100,
        serverId: file.id,
    }));
};

export const FileUpload: React.FC<FileUploadProps> = ({
    uploadList,
    uploadEndpoint,
    maxFiles = 10,
    maxSizePerFile = 5 * 1024 * 1024, // 5MB
    maxTotalSize = MAX_UPLOAD_BYTES,
    onFilesChange,
    disabled = false,
    initial = [],
    additionalFormData,
}) => {
    const [files, setFiles] = useState<UploadedFile[]>(loadInitialFiles(initial));
    const [showRemoveConfirm, setShowRemoveConfirm] = useState<string | null>(null);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);

    useEffect(() => {
        onFilesChange?.(files);
    }, [files]); // eslint-disable-line react-hooks/exhaustive-deps

    const updateFile = useCallback((fileId: string, updates: Partial<UploadedFile>) => {
        setFiles(prev => prev.map(f => (f.id === fileId ? { ...f, ...updates } : f)));
    }, []);

    const uploadFile = useCallback(
        async (file: File) => {
            const fileId = `${Date.now()}-${Math.random()}`;
            const uploadedFile: UploadedFile = {
                id: fileId,
                name: file.name,
                size: file.size,
                status: "uploading",
                progress: 0,
            };

            setFiles(prev => [...prev, uploadedFile]);

            try {
                const formData = new FormData();
                formData.append("file", file);
                if (additionalFormData) {
                    Object.entries(additionalFormData).forEach(([key, value]) => {
                        formData.append(key, value);
                    });
                }

                const response = await axios.post(uploadEndpoint, formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                    onUploadProgress: progressEvent => {
                        if (progressEvent.total) {
                            const progress = Math.round(
                                (progressEvent.loaded * 100) / progressEvent.total,
                            );
                            updateFile(fileId, { progress });
                        }
                    },
                });

                const serverId = response.data?.id as FileId | undefined;
                updateFile(fileId, { status: "completed", progress: 100, serverId });
            } catch (error) {
                const errorMessage = axios.isAxiosError(error)
                    ? error.response?.data?.message || error.message
                    : "Upload failed";
                updateFile(fileId, { status: "error", error: errorMessage });
            }
        },
        [uploadEndpoint, updateFile, additionalFormData],
    );

    const removeFile = useCallback((fileId: string) => {
        setFiles(prev => prev.filter(f => f.id !== fileId));
        setShowRemoveConfirm(null);
    }, []);

    const validateFiles = useCallback(
        (filesToValidate: File[]) => {
            const errors: string[] = [];

            // Check if adding these files would exceed max files
            if (files.length + filesToValidate.length > maxFiles) {
                errors.push(`You can only upload up to ${maxFiles} files total`);
            }

            // Check individual file size
            const oversizedFiles = filesToValidate.filter(f => f.size > maxSizePerFile);
            if (oversizedFiles.length > 0) {
                errors.push(
                    `Some files are too large. Maximum size per file is ${formatFileSize(
                        maxSizePerFile,
                    )}`,
                );
            }

            // Check total size
            const currentTotalSize = files.reduce((sum, f) => sum + f.size, 0);
            const newTotalSize = filesToValidate.reduce((sum, f) => sum + f.size, 0);
            if (currentTotalSize + newTotalSize > maxTotalSize) {
                errors.push(
                    `Total file size would exceed ${formatFileSize(maxTotalSize)} limit`,
                );
            }

            return errors;
        },
        [files, maxFiles, maxSizePerFile, maxTotalSize],
    );

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            if (disabled) return;

            const errors = validateFiles(acceptedFiles);
            if (errors.length > 0) {
                setValidationErrors(errors);
                return;
            }

            // Clear any existing errors
            setValidationErrors([]);

            // Upload each file in parallel
            acceptedFiles.forEach(uploadFile);
        },
        [disabled, validateFiles, uploadFile],
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        disabled,
        multiple: true,
        maxFiles: maxFiles - files.length,
    });

    const canUploadMore = files.length < maxFiles && !disabled;
    const uploadingCount = files.filter(f => f.status === "uploading").length;
    const completedCount = files.filter(f => f.status === "completed").length;
    const errorCount = files.filter(f => f.status === "error").length;

    return (
        <div className="space-y-4">
            {/* Upload Zone */}
            {canUploadMore && (
                <div
                    {...getRootProps()}
                    className={`
                        border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
                        ${
                            isDragActive
                                ? "border-blue-400 bg-blue-50"
                                : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
                        }
                        ${disabled ? "opacity-50 cursor-not-allowed" : ""}
                    `}>
                    <input {...getInputProps()} />
                    <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-600">
                        {isDragActive ? (
                            "Drop files here..."
                        ) : (
                            <>
                                <span className="font-medium text-blue-600 hover:text-blue-500">
                                    Click to upload
                                </span>
                                {" or drag and drop"}
                            </>
                        )}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                        Up to {maxFiles} files, {formatFileSize(maxSizePerFile)} each
                    </p>
                </div>
            )}

            {/* Validation Errors */}
            {validationErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex">
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">
                                Upload failed
                            </h3>
                            <div className="mt-2 text-sm text-red-700">
                                <ul className="list-disc pl-5 space-y-1">
                                    {validationErrors.map((error, index) => (
                                        <li key={index}>{error}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                        <div className="ml-auto pl-3">
                            <button
                                type="button"
                                onClick={() => setValidationErrors([])}
                                className="inline-flex text-gray-400 hover:text-gray-500"
                                aria-label="Dismiss">
                                <span className="sr-only">Dismiss</span>
                                <svg
                                    className="h-5 w-5"
                                    viewBox="0 0 20 20"
                                    fill="currentColor">
                                    <path
                                        fillRule="evenodd"
                                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {uploadList === "transient" ? (
                <UploadStatusList files={files} onDismiss={removeFile} />
            ) : null}

            {uploadList === "persistent" && files.length > 0 && (
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-900">
                            Uploaded Files ({completedCount}/{files.length})
                        </h4>
                        {uploadingCount > 0 && (
                            <span className="text-xs text-blue-600">
                                {uploadingCount} uploading...
                            </span>
                        )}
                    </div>

                    <div className="space-y-2">
                        {files.map(file => (
                            <div
                                key={file.id}
                                className="flex items-center p-3 border border-gray-200 rounded-lg">
                                <div className="flex-shrink-0">
                                    {file.status === "completed" ? (
                                        <CheckCircleIcon className="h-5 w-5 text-green-500" />
                                    ) : file.status === "error" ? (
                                        <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                                    ) : (
                                        <DocumentIcon className="h-5 w-5 text-blue-500" />
                                    )}
                                </div>

                                <div className="ml-3 flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                        {file.name}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        {formatFileSize(file.size)}
                                    </p>

                                    {file.status === "uploading" && (
                                        <div className="mt-1">
                                            <div className="flex items-center">
                                                <div className="flex-1 bg-gray-200 rounded-full h-2">
                                                    <div
                                                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                                        style={{
                                                            width: `${file.progress}%`,
                                                        }}
                                                    />
                                                </div>
                                                <span className="ml-2 text-xs text-gray-500">
                                                    {file.progress}%
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {file.status === "error" && file.error && (
                                        <p className="text-xs text-red-600 mt-1">
                                            {file.error}
                                        </p>
                                    )}
                                </div>

                                <div className="ml-3 flex-shrink-0">
                                    {file.status === "uploading" ? (
                                        <Spinner />
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => setShowRemoveConfirm(file.id)}
                                            className="text-gray-400 hover:text-red-500 transition-colors"
                                            title="Remove file">
                                            <TrashIcon className="h-4 w-4" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Remove Confirmation Modal */}
            {showRemoveConfirm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
                        <div className="flex items-center">
                            <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" />
                            <h3 className="ml-3 text-lg font-medium text-gray-900">
                                Remove File
                            </h3>
                        </div>
                        <p className="mt-2 text-sm text-gray-600">
                            Are you sure you want to remove this file? This action cannot
                            be undone.
                        </p>
                        <div className="mt-4 flex space-x-3">
                            <PrimaryButton
                                type="button"
                                onClick={() => removeFile(showRemoveConfirm)}
                                className="flex-1">
                                Remove
                            </PrimaryButton>
                            <SecondaryButton
                                type="button"
                                onClick={() => setShowRemoveConfirm(null)}
                                className="flex-1">
                                Cancel
                            </SecondaryButton>
                        </div>
                    </div>
                </div>
            )}

            {/* Summary for screen readers */}
            <div className="sr-only" aria-live="polite">
                {uploadingCount > 0 && `${uploadingCount} files uploading`}
                {completedCount > 0 && `${completedCount} files uploaded successfully`}
                {errorCount > 0 && `${errorCount} files failed to upload`}
            </div>
        </div>
    );
};
